// @ts-strict-ignore
import * as React from 'react';
import clsx from 'clsx';

import {ButtonReact} from 'common/components/button';
import {ButtonSize, ButtonTheme, ButtonType} from 'common/components/button/constants';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {
  generateCountCalculationMenuOptions,
  getSelectedCountBy,
  isAdvancedValueCalculation,
} from 'common/query-builder/query-block/count-menu/util';
import {getMultiAttributionFromEntry} from 'common/query-builder/query-builder/util';
import {CountUsersOnceState} from 'common/report/constants';
import {COUNT_USERS_ONCE_TYPE, MetricType} from 'common/types/reports/bookmark';
import {isScdPropertyName} from 'common/report/util/properties';
import {preventTracking} from 'common/util/dom';
import {useTranslation} from 'common/i18n/hooks';
import {FeatureRolloutName} from 'common/report/util/feature-sampling';
import {useFeatureRollout} from 'common/hooks/useFeatureRollout';
import {RECURRING_REVENUE_CHANGE} from 'common/query-builder/query-block/properties-menu/constants';
import {CountQueryBlock} from 'common/query-builder/query-block/count-menu/count-query-block';
import {
  Calculation,
  CalculationTypes,
  CountBy,
  InsightsCountType,
  isEventBasedEntry,
  isQueryUserEntry,
  isUserBasedEntry,
  MetricEntryMeasurement,
} from 'common/query-builder/query-entry-section/types';
import {
  getQueryEntryUpdateFromCalculation,
  getSelectedCalculation,
  getSelectedCalculationLabel,
  isProfileScdAggregation,
} from 'common/query-builder/query-entry-section/util';
import {QueryEntrySectionTrackingEvent} from 'common/query-builder/query-entry-section/tracking';
import {MetricEntry} from 'common/query-builder/query-entry-section/types';
import type {RowProps} from 'common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import {getEventNamesFromEntries} from 'common/query-builder/query-entry-section/row-types/row-util';

import styles from './index.module.styl';

export type MetricCountBySelectionProps = RowProps & {
  onMetricCountBySelected?: (countBy: CountBy) => void;
};

/**
 * Provides the selection menu for changing a Metric's measurement config
 */
export function MetricCountBySelection(props: MetricCountBySelectionProps) {
  const isRevenueEnabled = useFeatureRollout(FeatureRolloutName.Revenue);
  const {t, print} = useTranslation();

  const {behavior, measurement} = props.entry;

  const dateUnit = props.sectionProps.dateUnit;
  const plotStyle = props.sectionProps.plotStyle;
  const isUserBased = isUserBasedEntry(props.entry);
  const dataGroupId = behavior.dataGroupId ?? measurement.dataGroupId;

  const selectedCalculation = getSelectedCalculation(measurement);

  const selectedCalculationLabel = getSelectedCalculationLabel(selectedCalculation, dateUnit);

  const calculationSelectorOptions =
    (isUserBased && !isProfileScdAggregation(props.entry)) || props.entry.behavior.type === MetricType.Funnel
      ? []
      : generateCountCalculationMenuOptions({
          selectedPlotStyle: plotStyle,
          selectedCalculation,
          selectedDateUnit: dateUnit,
        });

  const handleMeasurementUpdate = (updatedMeasurement: Partial<MetricEntryMeasurement>) => {
    const entry: MetricEntry = {
      ...props.entry,
      measurement: {
        ...props.entry.measurement,
        ...updatedMeasurement,
      },
    };

    props.updateEntry(entry);
    props.dispatchTrack(QueryEntrySectionTrackingEvent.SelectNumericAggregator, {
      /** @todo(metrics) tracking */
      // ...props.getEntryTrackingProps(entry),
      Aggregator: entry.measurement.aggregationOperator,
      'Per-user aggregator': entry.measurement.aggregationOperatorPerUser,
      'Rolling window size': entry.measurement.rolling?.rollingWindowSize,
      Cumulative: Boolean(entry.measurement.cumulative),
      ...(entry.measurement.property && {
        'Property name': entry.measurement.property.label,
        'Property data type': `type` in entry.measurement.property && entry.measurement.property.type,
      }),
    });
  };

  return (
    <>
      <CountQueryBlock
        className={clsx(`subitem-style`, styles.countBlock)}
        elref='countBlock'
        // TODO: Cleaner way to do this?
        metricType={props.entry.behavior.type === MetricType.Simple ? MetricType.Event : props.entry.behavior.type}
        advancedMenuSections={calculationSelectorOptions}
        allDataGroups={props.sectionProps.dataGroupsEnabled}
        countUsersOnceState={
          getMultiAttributionFromEntry(props.entry)
            ? CountUsersOnceState.Required
            : props.sectionProps.countUsersOnceState
        }
        chartType={props.sectionProps.chartType}
        dataGroupId={props.sectionProps.dataGroupId || dataGroupId}
        dataGroups={props.dataGroups}
        dateRange={props.sectionProps.dateRange as any}
        dateUnit={props.sectionProps.dateUnit}
        enableWorkspaceFilterRestrictions={props.sectionProps.enableWorkspaceFilterRestrictions}
        /** @todo(metrics) extract event names from behaviors */
        eventNames={getEventNamesFromEntries(props.entry)}
        includeCustomProperties={props.sectionProps.includeCustomProperties}
        showBorrowedProperties={props.sectionProps.showBorrowedProperties}
        hideEvents={!isEventBasedEntry(props.entry)}
        otherAggregationOptions={[
          ...(props.sectionProps.otherAggregationOptions || []),
          isRevenueEnabled && isUserBased && RECURRING_REVENUE_CHANGE,
        ].filter(Boolean)}
        hideEditControls={props.sectionProps.hideEditControls}
        projectHasPeople={props.sectionProps.projectHasPeople}
        projectId={props.sectionProps.projectId}
        queryOrigin={props.sectionProps.queryOrigin}
        readonly={behavior.type === MetricType.Cohort}
        selected={getSelectedCountBy(props.entry)}
        trackName={props.sectionProps.trackName}
        userId={props.sectionProps.userId}
        workspaceId={props.sectionProps.workspaceId}
        onSelect={
          props.onMetricCountBySelected
            ? props.onMetricCountBySelected
            : (countBy) => {
                const {aggregationOperatorPerUser = null, property = null, advancedMenuValue = null} = countBy;
                let {rolling, cumulative, countUsersOnceType} = props.entry.measurement;
                if (advancedMenuValue && isAdvancedValueCalculation(advancedMenuValue)) {
                  // commited from advanced menu (changed calculation type)
                  const calculationType = advancedMenuValue.calculationType;
                  if (calculationType === CalculationTypes.Rolling) {
                    rolling = {rollingWindowSize: advancedMenuValue.rollingWindowSize};
                    cumulative = false;
                  } else if (calculationType === CalculationTypes.Cumulative) {
                    rolling = null;
                    cumulative = true;
                  }
                }

                if (Object.values(COUNT_USERS_ONCE_TYPE).some((type) => type === countBy.countUsersOnceType)) {
                  countUsersOnceType = countBy.countUsersOnceType;
                }

                if (
                  countBy.countType !== InsightsCountType.UniqueUsers ||
                  countUsersOnceType === COUNT_USERS_ONCE_TYPE.MULTIPLE
                ) {
                  countUsersOnceType = null;
                }

                if (
                  isQueryUserEntry(props.entry) &&
                  property &&
                  !(property.value === RECURRING_REVENUE_CHANGE.value || isScdPropertyName(property.value))
                ) {
                  rolling = null;
                  cumulative = false;
                }

                handleMeasurementUpdate({
                  aggregationOperator: countBy.aggregationOperator,
                  aggregationOperatorPerUser,
                  percentile: countBy.percentile,
                  property,
                  rolling,
                  cumulative: Boolean(cumulative),
                  countUsersOnceType,
                });
              }
        }
        withDataGroupSelect={props.sectionProps.metricLevelDataGroups}
        onTrack={preventTracking}
      />
      {selectedCalculation.calculationType && (
        <div className={styles.calculationSelector}>
          <SelectReact
            key='calculation-selector'
            elref='calculationSelector'
            label={print(selectedCalculationLabel)}
            disableSearch
            menuWidth={180}
            size={ButtonSize.Small}
            trackName={props.sectionProps.trackName}
            theme={SelectTheme.Tertiary}
            disableTooltip
            onChange={(ev: CustomEvent) => {
              handleMeasurementUpdate(
                getQueryEntryUpdateFromCalculation({
                  originalCalculation: props.entry.measurement,
                  calculation: ev.detail.item.value as Calculation,
                }),
              );
            }}
            sections={calculationSelectorOptions}
          />
          <ButtonReact
            className={styles.removeButton}
            theme={ButtonTheme.Tertiary}
            size={ButtonSize.Small}
            type={ButtonType.Destructive}
            tooltip={{
              placement: Placement.RightCenter,
              size: DisplaySize.Small,
              text: t(`query-builder.actions.remove`),
            }}
            icon='x'
            onClick={() => {
              handleMeasurementUpdate({
                rolling: null,
                cumulative: null,
              });
            }}
          />
        </div>
      )}
    </>
  );
}
